import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { BarChart3, Download, LineChart as LineChartIcon } from "lucide-react";
import { ConfigProvider, DatePicker, theme as antTheme } from "antd";
import dayjs from "dayjs";
import { useTheme } from "../../context/ThemeContext";
import { getDroughtTrendAnalysis } from "../../services";

const rangeOptions = [
  { label: "D", value: "D" },
  { label: "W", value: "W" },
  { label: "1M", value: "1M" },
  { label: "3M", value: "3M" },
  { label: "6M", value: "6M" },
  { label: "YTD", value: "YTD" },
  { label: "1Y", value: "1Y" },
];

const getApiRange = (range) => {
  if (range === "1M") return "M";

  return range || "M";
};

const normalizeTrendResponse = (apiData = []) => {
  if (!Array.isArray(apiData) || apiData.length <= 1) return [];

  return apiData.slice(1).map((row) => ({
    label: row?.[0] ?? "-",
    currentViolations: Number(row?.[1] || 0),
    currentWarnings: Number(row?.[2] || 0),
    previousViolations: Number(row?.[3] || 0),
    previousWarnings: Number(row?.[4] || 0),
  }));
};

const DroughtTrendChart = () => {
  const { colors, theme } = useTheme();

  const isLightTheme = theme === "white";

const [selectedRange, setSelectedRange] = useState("1M");
  const [chartType, setChartType] = useState("line");

const [dateRange, setDateRange] = useState({
  start: dayjs().subtract(1, "month").format("YYYY-MM-DD"),
  end: dayjs().format("YYYY-MM-DD"),
});

  const [trendData, setTrendData] = useState([]);
  const [loadingTrend, setLoadingTrend] = useState(false);

  const activeRange = rangeOptions.find((item) => item.value === selectedRange);

  const apiRange = useMemo(() => getApiRange(selectedRange), [selectedRange]);

  useEffect(() => {
    const fetchTrendAnalysis = async () => {
      try {
        setLoadingTrend(true);

        const res = await getDroughtTrendAnalysis(apiRange);

        const apiData = Array.isArray(res?.data) ? res.data : [];

        setTrendData(normalizeTrendResponse(apiData));
      } catch (error) {
        console.error(
          "Drought trend analysis API error:",
          error.response?.data || error.message,
        );

        setTrendData([]);
      } finally {
        setLoadingTrend(false);
      }
    };

    fetchTrendAnalysis();
  }, [apiRange]);

const handleRangeClick = (item) => {
  setSelectedRange(item.value);

  const end = dayjs();
  let start = dayjs();

  switch (item.value) {
    case "D":
      start = end.subtract(1, "day");
      break;

    case "W":
      start = end.subtract(7, "day");
      break;

    case "1M":
      start = end.subtract(1, "month");
      break;

    case "3M":
      start = end.subtract(3, "month");
      break;

    case "6M":
      start = end.subtract(6, "month");
      break;

    case "YTD":
      start = end.startOf("year");
      break;

    case "1Y":
      start = end.subtract(1, "year");
      break;

    default:
      start = end.subtract(1, "month");
      break;
  }

  setDateRange({
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
  });
};

const handleFromChange = (date) => {
  setDateRange((prev) => ({
    ...prev,
    start: date ? date.format("YYYY-MM-DD") : null,
  }));
};

const handleToChange = (date) => {
  setDateRange((prev) => ({
    ...prev,
    end: date ? date.format("YYYY-MM-DD") : null,
  }));
};

const getPickerValue = (value) => {
  if (!value) return null;

  return dayjs(value, "YYYY-MM-DD");
};

  const formatTrendValue = (value) => {
    if (value === null || value === undefined) return "-";
    return Number(value || 0).toLocaleString("en-US");
  };

  const TrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    return (
      <div
        className="rounded-lg border px-4 py-3 text-xs shadow-xl"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
          color: colors.text,
        }}
      >
        <p className="mb-2 font-bold" style={{ color: colors.primary }}>
          {label}
        </p>

        <div className="space-y-2">
          {payload.map((item) => (
            <div
              key={item.dataKey}
              className="flex items-center justify-between gap-5"
            >
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: item.color }}
                />

                <span style={{ color: colors.textSecondary }}>
                  {item.name}
                </span>
              </div>

              <span className="font-bold" style={{ color: colors.text }}>
                {formatTrendValue(item.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const downloadTrendCSV = () => {
    if (!trendData.length) return;

    const headers = [
      "Label",
      "Current Violations",
      "Current Warnings",
      "Previous Violations",
      "Previous Warnings",
    ];

    const rows = trendData.map((item) => [
      item.label,
      item.currentViolations,
      item.currentWarnings,
      item.previousViolations,
      item.previousWarnings,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `drought-trend-${apiRange}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="mt-6 rounded-xl border p-6 shadow-xl"
      style={{
        backgroundColor: colors.cardBg,
        borderColor: colors.border,
      }}
    >
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-xl font-bold" style={{ color: colors.text }}>
            Trend
          </h3>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Existing D / W / M / Q / Y Buttons */}
          <div
            className="flex items-center gap-1 rounded-xl px-3 py-2"
            style={{ backgroundColor: colors.background }}
          >
            {rangeOptions.map((item) => {
              const isActive = selectedRange === item.value;

              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => handleRangeClick(item)}
                  className="h-7 min-w-7 rounded-lg px-2 text-xs font-extrabold transition-all"
                  style={{
                    backgroundColor: isActive ? colors.primary : "transparent",
                    color: isActive ? "#ffffff" : colors.textSecondary,
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Existing From Picker */}
          {/* <ConfigProvider
            theme={{
              algorithm: isLightTheme
                ? antTheme.defaultAlgorithm
                : antTheme.darkAlgorithm,
              token: {
                colorPrimary: colors.primary,
                colorBgContainer: colors.background,
                colorText: colors.text,
                colorTextPlaceholder: colors.textSecondary,
                colorBorder: "transparent",
                controlHeight: 40,
                borderRadius: 12,
              },
            }}
          >
           <DatePicker
  value={getPickerValue(dateRange.start)}
  onChange={handleFromChange}
  disabledDate={(current) => current && current > dayjs().endOf("day")}
  placeholder="From"
  variant="borderless"
  allowClear={false}
  className="custom-range-picker cursor-pointer rounded-xl px-3 py-2"
  style={{
    width: "130px",
    background: colors.background,
    color: colors.text,
  }}
  classNames={{
    popup: {
      root: "mizu-calendar-popup",
    },
  }}
/>
          </ConfigProvider> */}

          {/* <span style={{ color: colors.textSecondary }}>-</span> */}

          {/* Existing To Picker */}
          {/* <ConfigProvider
            theme={{
              algorithm: isLightTheme
                ? antTheme.defaultAlgorithm
                : antTheme.darkAlgorithm,
              token: {
                colorPrimary: colors.primary,
                colorBgContainer: colors.background,
                colorText: colors.text,
                colorTextPlaceholder: colors.textSecondary,
                colorBorder: "transparent",
                controlHeight: 40,
                borderRadius: 12,
              },
            }}
          >
           <DatePicker
  value={getPickerValue(dateRange.end)}
  onChange={handleToChange}
  disabledDate={(current) => current && current > dayjs().endOf("day")}
  placeholder="To"
  variant="borderless"
  allowClear={false}
  className="custom-range-picker cursor-pointer rounded-xl px-3 py-2"
  style={{
    width: "130px",
    background: colors.background,
    color: colors.text,
  }}
  classNames={{
    popup: {
      root: "mizu-calendar-popup",
    },
  }}
/>
          </ConfigProvider> */}

          <button
            type="button"
            onClick={downloadTrendCSV}
            disabled={!trendData.length}
            className="rounded-xl border p-3 transition-all hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
              color: colors.text,
            }}
            title="Download CSV"
          >
            <Download size={18} />
          </button>

          <button
            type="button"
            onClick={() =>
              setChartType((prev) => (prev === "line" ? "bar" : "line"))
            }
            className="rounded-xl border p-3 transition-all hover:opacity-80"
            style={{
              backgroundColor:
                chartType === "bar" ? `${colors.primary}20` : colors.cardBg,
              borderColor: chartType === "bar" ? colors.primary : colors.border,
              color: chartType === "bar" ? colors.primary : colors.text,
            }}
            title={
              chartType === "line"
                ? "Switch to Bar Chart"
                : "Switch to Line Chart"
            }
          >
            {chartType === "line" ? (
              <BarChart3 size={18} />
            ) : (
              <LineChartIcon size={18} />
            )}
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="h-[420px] w-full">
        {loadingTrend ? (
          <div
            className="flex h-full items-center justify-center text-sm font-semibold"
            style={{ color: colors.textSecondary }}
          >
            Loading trend...
          </div>
        ) : trendData.length === 0 ? (
          <div
            className="flex h-full items-center justify-center text-sm font-semibold"
            style={{ color: colors.textSecondary }}
          >
            No trend data found.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "line" ? (
              <LineChart
                data={trendData}
                margin={{
                  top: 20,
                  right: 24,
                  left: 10,
                  bottom: 20,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={colors.border}
                  opacity={0.55}
                />

                <XAxis
                  dataKey="label"
                  tick={{
                    fill: colors.textSecondary,
                    fontSize: 12,
                  }}
                  axisLine={{
                    stroke: colors.border,
                  }}
                  tickLine={false}
                />

                <YAxis
                  allowDecimals={false}
                  tick={{
                    fill: colors.textSecondary,
                    fontSize: 12,
                  }}
                  axisLine={{
                    stroke: colors.border,
                  }}
                  tickLine={false}
                  tickFormatter={(value) => formatTrendValue(value)}
                  label={{
                    value: "Count",
                    angle: -90,
                    position: "insideLeft",
                    fill: colors.textSecondary,
                    fontSize: 13,
                    dy: 30,
                  }}
                />

                <Tooltip content={<TrendTooltip />} />

                <Legend
                  verticalAlign="bottom"
                  height={45}
                  iconType="square"
                  formatter={(value) => (
                    <span
                      style={{
                        color: colors.textSecondary,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {value}
                    </span>
                  )}
                />

                <Line
                  type="monotone"
                  dataKey="currentViolations"
                  name="Current Violations"
                  stroke="#ef4444"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 2 }}
                  connectNulls={false}
                />

                <Line
                  type="monotone"
                  dataKey="currentWarnings"
                  name="Current Warnings"
                  stroke="#f59e0b"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 2 }}
                  connectNulls={false}
                />

                <Line
                  type="monotone"
                  dataKey="previousViolations"
                  name="Previous Violations"
                  stroke="#fca5a5"
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 2 }}
                  connectNulls={false}
                />

                <Line
                  type="monotone"
                  dataKey="previousWarnings"
                  name="Previous Warnings"
                  stroke="#fcd34d"
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 2 }}
                  connectNulls={false}
                />
              </LineChart>
            ) : (
              <BarChart
                data={trendData}
                margin={{
                  top: 20,
                  right: 24,
                  left: 10,
                  bottom: 20,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={colors.border}
                  opacity={0.55}
                />

                <XAxis
                  dataKey="label"
                  tick={{
                    fill: colors.textSecondary,
                    fontSize: 12,
                  }}
                  axisLine={{
                    stroke: colors.border,
                  }}
                  tickLine={false}
                />

                <YAxis
                  allowDecimals={false}
                  tick={{
                    fill: colors.textSecondary,
                    fontSize: 12,
                  }}
                  axisLine={{
                    stroke: colors.border,
                  }}
                  tickLine={false}
                  tickFormatter={(value) => formatTrendValue(value)}
                  label={{
                    value: "Count",
                    angle: -90,
                    position: "insideLeft",
                    fill: colors.textSecondary,
                    fontSize: 13,
                    dy: 30,
                  }}
                />

                <Tooltip content={<TrendTooltip />} />

                <Legend
                  verticalAlign="bottom"
                  height={45}
                  iconType="square"
                  formatter={(value) => (
                    <span
                      style={{
                        color: colors.textSecondary,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {value}
                    </span>
                  )}
                />

                <Bar
                  dataKey="currentViolations"
                  name="Current Violations"
                  fill="#ef4444"
                  radius={[6, 6, 0, 0]}
                />

                <Bar
                  dataKey="currentWarnings"
                  name="Current Warnings"
                  fill="#f59e0b"
                  radius={[6, 6, 0, 0]}
                />

                <Bar
                  dataKey="previousViolations"
                  name="Previous Violations"
                  fill="#fca5a5"
                  radius={[6, 6, 0, 0]}
                />

                <Bar
                  dataKey="previousWarnings"
                  name="Previous Warnings"
                  fill="#fcd34d"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default DroughtTrendChart;